import Events from '../../utilities/EventEmitter';
import appConstants from '../../appConstants';
import { throttle } from 'lodash';
import { APP_EVENT_NAMES } from '../constants';

/**
 * Initial GPS batch interval (10s) used on first load before parameters are applied.
 * Keeps location flushes infrequent while the map and dispatch context finish initializing,
 * so we do not drive UI updates from pings too early. Replaced as soon as the API provides
 * `BatchLocationUpdateIntervalInSeconds` (typically 5s) via setBatchLocationUpdateInterval.
 */
const DEFAULT_BATCH_LOCATION_INTERVAL_MS = appConstants.ONE_SECOND_TIME_OUT * 10;

const driverLocationMap = new Map();
let locationUpdateControls = {
  shouldRunBatchUpdate: false,
  currentTerminalCode: null,
  intervalInMilliseconds: DEFAULT_BATCH_LOCATION_INTERVAL_MS,
};

const setDriverLocation = (driverId, data) => {
  driverLocationMap.set(driverId?.toUpperCase(), data);
};

const deleteDriversLocationCache = (driverIds) => {
  driverIds.forEach((driverId) => {
    driverLocationMap.delete(driverId?.toUpperCase());
  });
};

const clearDriverLocationMappingCache = () => {
  driverLocationMap.clear();
};

const createBatchCycle = (intervalMs) =>
  throttle(() => {
    if (driverLocationMap.size !== 0 && locationUpdateControls.shouldRunBatchUpdate) {
      Events.emit(APP_EVENT_NAMES.DISPATCH.AddDriverLocation, new Map(driverLocationMap));
      clearDriverLocationMappingCache();
    }
  }, intervalMs);

// Throttle delay is fixed when created — changing a variable later does not update it.
// So we replace `startBatchLocationUpdateCycle` with a new `createBatchCycle(ms)` whenever `ms` changes.
let startBatchLocationUpdateCycle = createBatchCycle(DEFAULT_BATCH_LOCATION_INTERVAL_MS);

// Update the batch interval on terminal change based on a parameter from the API.
const setBatchLocationUpdateInterval = (intervalInMilliseconds) => {
  locationUpdateControls.intervalInMilliseconds = intervalInMilliseconds;
  startBatchLocationUpdateCycle = createBatchCycle(intervalInMilliseconds);
};

// Emit batch driver location update event to listeners
const setBatchLocationUpdateConfig = (locationUpdateProps) => {
  clearDriverLocationMappingCache();
  locationUpdateControls = {...locationUpdateControls, ...locationUpdateProps};
};

const locationUpdated = (terminalCode, driverId, lat, lng, eventTime) => {
  if (
    !locationUpdateControls.shouldRunBatchUpdate ||
    locationUpdateControls.currentTerminalCode !== terminalCode
  ) {
    return;
  }
  startBatchLocationUpdateCycle();
  setDriverLocation(driverId, { terminalCode, lat, lng, eventTime });
};

export {
  locationUpdated,
  setBatchLocationUpdateConfig,
  clearDriverLocationMappingCache,
  deleteDriversLocationCache,
  setBatchLocationUpdateInterval,
};
